import React, { useEffect, useState } from "react";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";

interface EvolutionStats {
  ability_power: number;
  resistance: number;
  speed: number;
}

interface EvolutionAnimationScreenProps {
  isOpen: boolean;
  fromPokemonName: string;
  toPokemonName: string;
  oldStats: EvolutionStats;
  newStats: EvolutionStats;
  onComplete: () => void;
}

const StatBar = ({
  label,
  oldValue,
  newValue,
  variant,
  delay,
}: {
  label: string;
  oldValue: number;
  newValue: number;
  variant: "red" | "blue" | "yellow";
  delay: number;
}) => {
  const [show, setShow] = useState(false);
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
      // Animate value
      const diff = newValue - oldValue;
      const steps = 20;
      const duration = 1000;
      const stepTime = duration / steps;
      const valStep = diff / steps;

      let current = oldValue;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        current += valStep;
        if (step >= steps) {
          setCurrentValue(newValue);
          clearInterval(interval);
        } else {
          setCurrentValue(Math.round(current));
        }
      }, stepTime);

      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, oldValue, newValue]);

  if (!show) return <div className="h-12" />; // Placeholder

  const diff = newValue - oldValue;
  // Max stat visualization (arbitrary scaling)
  const maxStat = 250;

  return (
    <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500 mb-2 px-6">
      <div className="flex justify-between text-white mb-1 px-1">
        <StrokedText fontSize={16} strokeWidth={3}>
          {label}
        </StrokedText>
        <div className="flex gap-2">
          <span className="text-white/70">{oldValue}</span>
          <span className="text-pk-white">→</span>
          <span className="text-white font-bold">{currentValue}</span>
          <span className="text-pk-green-3 font-bold">(+{diff})</span>
        </div>
      </div>

      <SegmentedProgressBar
        ratio={currentValue / maxStat}
        variant={variant}
        segmented={false}
      />
    </div>
  );
};

export const EvolutionAnimationScreen = ({
  isOpen,
  fromPokemonName,
  toPokemonName,
  oldStats,
  newStats,
  onComplete,
}: EvolutionAnimationScreenProps) => {
  const [step, setStep] = useState<"intro" | "evolving" | "revealed">("intro");
  const [displayPokemon, setDisplayPokemon] = useState(fromPokemonName);
  const [isFlashing, setIsFlashing] = useState(false);
  const [flashIntensity, setFlashIntensity] = useState(0); // 0 to 1
  const [scale, setScale] = useState(1);
  const [message, setMessage] = useState("");
  const [showContinue, setShowContinue] = useState(false);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStep("intro");
      setDisplayPokemon(fromPokemonName);
      setIsFlashing(false);
      setFlashIntensity(0);
      setScale(1);
      setMessage(`What?\n ${fromPokemonName.toUpperCase()} is evolving!`);
      setShowContinue(false);

      // Start evolution sequence
      const sequence = async () => {
        // 1. Wait in intro
        await new Promise((r) => setTimeout(r, 2000));

        setStep("evolving");

        // 2. Slow flashes
        for (let i = 0; i < 3; i++) {
          setIsFlashing(true);
          await new Promise((r) => setTimeout(r, 400));
          setIsFlashing(false);
          await new Promise((r) => setTimeout(r, 400));
        }

        // 3. Fast flashes + Scaling
        for (let i = 0; i < 8; i++) {
          setIsFlashing(true);
          setScale(1 + i * 0.05); // Grow slightly
          await new Promise((r) => setTimeout(r, 150));
          setIsFlashing(false);
          setScale(1 - i * 0.02); // Shrink slightly
          await new Promise((r) => setTimeout(r, 150));
        }

        // 4. Big Flash and Swap
        setFlashIntensity(1); // Full screen white
        await new Promise((r) => setTimeout(r, 100));

        setDisplayPokemon(toPokemonName);
        setScale(1);

        // Fade out white
        await new Promise((r) => setTimeout(r, 1000));
        setFlashIntensity(0);

        setStep("revealed");
        // Just clear message for the new UI
        setMessage("");

        // Allow continue after a moment
        setTimeout(() => setShowContinue(true), 2000);
      };

      sequence();
    }
  }, [isOpen, fromPokemonName, toPokemonName]);

  if (!isOpen) return null;

  // Format name for display
  const formatName = (name: string) =>
    name
      .split("-")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,rgba(0,0,0,1)_70%)]" />

      {/* Pokemon Display */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full h-full">
        {/* Name (only shown when revealed) */}
        {step === "revealed" && (
          <div className="absolute top-16 z-20 animate-in zoom-in duration-1000">
            <StrokedText
              fontSize={48}
              strokeWidth={6}
              className="text-pk-yellow-2 uppercase tracking-wider filter drop-shadow-[0_0_10px_rgba(255,215,0,0.5)]"
            >
              {formatName(toPokemonName)}
            </StrokedText>
          </div>
        )}

        {/* Sprite Area */}
        <div className="relative flex items-center justify-center h-[300px] w-[300px] mb-20">
          {/* VFX Layer */}
          {step === "revealed" && (
            <>
              <AnimatedVFX
                id="evo-burst"
                spriteUrl="/assets/spells/tile-fx/magic-puff-white.png"
                position={{ x: 150, y: 150 }}
                spriteScale={6}
                className="pointer-events-none"
              />
              <AnimatedVFX
                id="evo-stars"
                spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
                position={{ x: 250, y: 200 }}
                spriteScale={2}
                loops={2}
                className="pointer-events-none opacity-80"
              />
            </>
          )}

          <div
            className="transition-all duration-100"
            style={{
              transform: `scale(${scale})`,
              filter: isFlashing ? "brightness(0) invert(1)" : "none",
            }}
          >
            <PokemonSprite
              pokemon={{ base_pokemon_name: displayPokemon }}
              style={{ scale: 5 }}
              animation="Idle"
            />
          </div>
        </div>

        {/* Bottom Section: Message OR Stats */}
        <div className="absolute bottom-12 left-0 right-0 pb-12 pt-20 px-6 bg-gradient-to-t from-black via-black/90 to-transparent z-20 min-h-[250px]">
          {step === "revealed" ? (
            <div className="max-w-md mx-auto w-full space-y-1">
              <StatBar
                label="Ability Power"
                oldValue={oldStats.ability_power}
                newValue={newStats.ability_power}
                variant="red"
                delay={500}
              />
              <StatBar
                label="Resistance"
                oldValue={oldStats.resistance}
                newValue={newStats.resistance}
                variant="blue"
                delay={1200}
              />
              <StatBar
                label="Dexterity"
                oldValue={oldStats.speed}
                newValue={newStats.speed}
                variant="yellow"
                delay={1900}
              />

              {showContinue && (
                <div
                  className="mt-6 text-center text-pk-yellow-2 animate-bounce cursor-pointer text-lg font-bold tracking-widest uppercase"
                  onClick={onComplete}
                >
                  ▼ Continue
                </div>
              )}
            </div>
          ) : (
            <div className="text-center w-full pb-12">
              <StrokedText
                fontSize={24}
                strokeWidth={4}
                className="text-white tracking-wide"
              >
                {message}
              </StrokedText>
            </div>
          )}
        </div>
      </div>

      {/* Full Screen White Flash Overlay */}
      <div
        className="absolute inset-0 bg-white pointer-events-none transition-opacity duration-1000 z-50"
        style={{ opacity: flashIntensity }}
      />

      {/* Click anywhere handler for revealed state */}
      {step === "revealed" && showContinue && (
        <div
          className="absolute inset-0 z-30 cursor-pointer"
          onClick={onComplete}
        />
      )}
    </div>
  );
};
